import React, { FC, PropsWithChildren } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CrossFaderProps } from './types';
import { useCrossFaderStyles } from './styles';

/**
 * Fades in the currently shown component while fading out the previously shown component.
 * Components that are not visible will be unmounted, after the fade out animation finishes.
 * One thing to note is that if components higher up in the component hierarchy remount during the transition the `CrossFader` component will be unable to animate the transition.
 */

export const CrossFader: FC<PropsWithChildren<CrossFaderProps>> = ({
	show,
	components,
	alignHorizontal = 'left',
	alignVertical = 'top',
	width = 'inherit',
	height = 'inherit',
}) => {
	const { classes, cx } = useCrossFaderStyles();
	const appliedClasses = cx(classes.crossfader, {
		[classes.alignRight]: alignHorizontal === 'right',
		[classes.alignBottom]: alignVertical === 'bottom',
	});

	const dimensionStyles = {
		height,
		width,
	};

	return (
		<div className={appliedClasses} role="region" style={dimensionStyles}>
			<AnimatePresence initial={false}>
				{Object.entries(components).map(
					([key, component]) =>
						key === show && (
							<motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }} key={key}>
								{component}
							</motion.div>
						)
				)}
			</AnimatePresence>
		</div>
	);
};
